import Validator, { ValidationError, ValidationRuleObject, ValidationSchema } from 'fastest-validator';
import { KONTRAKT_VALIDATION_ERROR, NEUER_ARTIKEL_BEZEICHNUNG_VALIDATION_ERROR, NEWID_PREFIX, ZULAGE_VALIDATION_ERROR } from '../constants';
import { ArtikelTyp, Berechnungsart } from '../types/enums';
import type {
    BestellpositionArtikelLoseWareView,
    BestellpositionDetails,
    BestellpositionDetailsLoseWare,
    BestellpositionDetailsSackware,
} from '../types';

export type AuftragskorbBestellung = {
    loseWareBestellpositionen: BestellpositionDetails[];
    sackwareBestellpositionen: BestellpositionDetails[];
};

const validator = new Validator({
    useNewCustomCheckerFunction: true,
});

const bestellpositionArtikelnummerValidationSchema = {
    type: 'custom',
    check(
        value: string,
        errors: ValidationError[],
        schema: ValidationRuleObject,
        path: string,
        parent: BestellpositionArtikelLoseWareView | null
    ) {
        if (value.startsWith(NEWID_PREFIX) && !parent?.bezeichnung) {
            errors.push({ field: 'bezeichnung', message: NEUER_ARTIKEL_BEZEICHNUNG_VALIDATION_ERROR, type: 'string' });
        }
        return value;
    },
};

const bestellpositionBerechnungsartValidationSchema = {
    type: 'custom',
    optional: true,
    check(
        value: Berechnungsart,
        errors: ValidationError[],
        schema: ValidationRuleObject,
        path: string,
        parent: BestellpositionArtikelLoseWareView | null
    ) {
        if (value === Berechnungsart.Kontrakt && !parent?.kontrakt) {
            errors.push({ field: 'kontrakt', message: KONTRAKT_VALIDATION_ERROR, type: 'string' });
        }
        return value;
    },
};

const auftragskorbBestellungSchema = {
    $$async: false,
    loseWareBestellpositionen: {
        type: 'array',
        items: {
            type: 'object',
            props: {
                artikelTyp: { type: 'enum', values: [ArtikelTyp.LoseWare] },
                werkId: { type: 'string', empty: true },
                menge: [
                    { type: 'string', pattern: /^\d*(?:,\d)?$/, nullable: false },
                    { type: 'number', negative: false },
                ],
                zulage: {
                    type: 'custom',
                    check(
                        value: boolean,
                        errors: ValidationError[],
                        schema: ValidationRuleObject,
                        path: string,
                        parent: BestellpositionArtikelLoseWareView | null
                    ) {
                        if (value && !parent?.zulageText) {
                            const hatArtikelDropdown = (parent as BestellpositionArtikelLoseWareView | null)?.zulageHatArtikelDropdown;
                            if (hatArtikelDropdown) {
                                return value;
                            }
                            errors.push({ field: 'zulageText', message: ZULAGE_VALIDATION_ERROR, type: 'string' });
                        }
                        return value;
                    },
                },
                berechnungsart: bestellpositionBerechnungsartValidationSchema,
                nummer: bestellpositionArtikelnummerValidationSchema,
            },
        },
    },
    sackwareBestellpositionen: {
        type: 'array',
        items: {
            type: 'object',
            props: {
                artikelTyp: { type: 'enum', values: [ArtikelTyp.Sackware] },
                werkId: { type: 'string', empty: true },
                mengeVe: [
                    { type: 'string', pattern: /^\d*(?:,\d)?$/, optional: true },
                    { type: 'number', negative: false, optional: true },
                ],
                mengePal: [
                    { type: 'string', pattern: /^\d*(?:,\d)?$/, optional: true },
                    { type: 'number', negative: false, optional: true },
                ],
                nummer: bestellpositionArtikelnummerValidationSchema,
            },
        },
    },
} as ValidationSchema<AuftragskorbBestellung>;

/**
 * Normalisiert BestellpositionDetails für die Validierung - konvertiert undefined/null zu erwarteten Typen
 */
const normalizeBestellpositionForValidation = (position: BestellpositionDetails): BestellpositionDetails => {
    if (position.artikelTyp === ArtikelTyp.LoseWare) {
        const lwPos = position as BestellpositionDetailsLoseWare;
        return {
            ...lwPos,
            werkId: lwPos.werkId != null ? String(lwPos.werkId) : '',
            zulage: lwPos.zulage != null ? Boolean(lwPos.zulage) : false,
            zulageText: lwPos.zulageText != null ? String(lwPos.zulageText) : '',
            berechnungsart: lwPos.berechnungsart != null ? lwPos.berechnungsart : Berechnungsart.KeinVorgabe,
        } as BestellpositionDetailsLoseWare;
    } else {
        const swPos = position as BestellpositionDetailsSackware;
        return {
            ...swPos,
            werkId: swPos.werkId != null ? String(swPos.werkId) : '',
        } as BestellpositionDetailsSackware;
    }
};

export const validateAuftragskorbBestellung = (auftragskorbBestellung: AuftragskorbBestellung): boolean => {
    // Normalisiere Positionen vor Validierung
    const normalizedBestellung: AuftragskorbBestellung = {
        loseWareBestellpositionen: auftragskorbBestellung.loseWareBestellpositionen.map(normalizeBestellpositionForValidation),
        sackwareBestellpositionen: auftragskorbBestellung.sackwareBestellpositionen.map(normalizeBestellpositionForValidation),
    };

    const errorsOrTrue = validator.validate(normalizedBestellung, auftragskorbBestellungSchema);

    // Logge Fehler für Debugging
    if (errorsOrTrue !== true) {
        console.group(`[validateAuftragskorbBestellung] Validierungsfehler:`);
        console.error('Fehler:', errorsOrTrue);
        if (Array.isArray(errorsOrTrue)) {
            errorsOrTrue.forEach((error, index) => {
                console.error(`Fehler ${index + 1}:`, {
                    field: error.field,
                    message: error.message,
                    type: error.type,
                    actual: error.actual,
                    expected: error.expected,
                });
            });
        }
        console.log('Normalisierte Daten:', normalizedBestellung);
        console.groupEnd();
    }

    return errorsOrTrue === true;
};
